import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShoppingBag, Download, Package, Loader2, X, Star, Store, Loader } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { SocialShare } from "@/components/SocialShare";
import CatalogImage from "@/components/CatalogImage";
import { sanitizeHtml } from "@/components/RichTextEditor";
import Seo, { stripHtml } from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import { GiftCardPurchase } from "@/components/GiftCardPurchase";

const HERO = "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CATEGORIES = ["All", "Wellness Tools", "Courses", "Music", "Art", "Gift Cards", "Enhancer Products"];
const MEMBER_ROLES = ["community", "enhancer", "staff", "council", "admin"];

const Stars = ({ value = 0, size = 14, onSelect }) => (
  <span className="inline-flex">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} onClick={onSelect ? () => onSelect(n) : undefined}
        className={`${onSelect ? "cursor-pointer" : ""} ${n <= Math.round(value) ? "fill-[#E0A458] text-[#E0A458]" : "text-[#D8D2C8]"}`} />
    ))}
  </span>
);

function FeaturedVendors() {
  const { data: vendors = [] } = useQuery({
    queryKey: ["featured-vendors"],
    queryFn: () => api.get("/vendors/featured").then((r) => r.data),
  });
  if (vendors.length === 0) return null;
  return (
    <section className="mb-12" data-testid="featured-vendors">
      <div className="flex items-center gap-2 mb-5">
        <Store size={20} className="text-[#B5794E]" />
        <h2 className="font-serif text-2xl text-[#1A242B]">Featured vendors</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {vendors.map((v) => (
          <Link key={v.id} to={`/enhancers/${v.id}`} data-testid={`featured-vendor-${v.id}`}
            className="eyw-card p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
            {v.avatar
              ? <img src={v.avatar} alt={v.name} className="h-14 w-14 rounded-full object-cover shrink-0" />
              : <span className="grid place-items-center h-14 w-14 rounded-full bg-[#B5794E]/12 text-[#B5794E] text-xl font-semibold shrink-0">{(v.vendor_business || v.name || "?").slice(0, 1).toUpperCase()}</span>}
            <div className="min-w-0">
              <h3 className="text-lg text-[#1A242B] truncate">{v.vendor_business || v.name}</h3>
              {v.bio_excerpt && <p className="text-sm text-[#4A5D6B] mt-1 line-clamp-2">{v.bio_excerpt}</p>}
              <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#B5794E] font-medium">
                <Package size={12} /> {v.product_count} {v.product_count === 1 ? "product" : "products"}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ProductReviews({ productId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const { data } = useQuery({
    queryKey: ["product-reviews", productId],
    queryFn: () => api.get(`/products/${productId}/reviews`).then((r) => r.data),
  });
  const mut = useMutation({
    mutationFn: () => api.post(`/products/${productId}/reviews`, { rating, comment }),
    onSuccess: () => {
      toast.success("Thanks for your review!");
      setComment("");
      qc.invalidateQueries({ queryKey: ["product-reviews", productId] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (rid) => api.delete(`/products/${productId}/reviews/${rid}`),
    onSuccess: () => { toast.success("Review removed"); qc.invalidateQueries({ queryKey: ["product-reviews", productId] }); qc.invalidateQueries({ queryKey: ["products"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not remove review"),
  });
  const reviews = data?.reviews || [];
  const canModerate = ["admin", "staff", "council"].includes(user?.role);
  return (
    <div className="mt-6 border-t border-[#E5E0D8] pt-5" data-testid="product-reviews">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-serif text-lg text-[#1A242B]">Reviews</h3>
        {data?.review_count > 0 && (
          <span className="inline-flex items-center gap-1.5 text-sm text-[#4A5D6B]">
            <Stars value={data.avg_rating} /> {data.avg_rating} · {data.review_count}
          </span>
        )}
      </div>
      {user ? (
        <div className="eyw-card p-4 mb-4" data-testid="product-review-composer">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm font-medium text-[#1A242B]">Your rating</span>
            <Stars value={rating} size={20} onSelect={setRating} />
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Share your thoughts on this product…"
            data-testid="product-review-comment" className="w-full rounded-lg border border-[#E5E0D8] px-3 py-2 text-sm outline-none focus:border-[#144F75] resize-none" />
          <div className="flex justify-end mt-2">
            <button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="product-review-submit" className="eyw-btn !py-2 text-sm disabled:opacity-60">
              {mut.isPending && <Loader className="animate-spin" size={15} />} Post review
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#4A5D6B] mb-4"><Link to="/login" className="text-[#144F75] underline">Sign in</Link> to leave a review.</p>
      )}
      {reviews.length === 0 ? (
        <p className="text-sm text-[#4A5D6B]">No reviews yet — be the first to review this product.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-lg bg-white border border-[#E5E0D8] p-3" data-testid={`product-review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#1A242B] text-sm">{r.author_name || "Member"}</span>
                  <Stars value={r.rating} size={13} />
                  {r.verified_purchase && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">Verified purchase</span>}
                </div>
                {(canModerate || r.author_id === user?.id) && (
                  <button onClick={() => del.mutate(r.id)} data-testid={`product-review-del-${r.id}`} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                )}
              </div>
              {r.comment && <p className="text-sm text-[#4A5D6B] mt-1.5 leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, isMember, onAdd }) {
  const hasVariants = product.variants && product.variants.length > 0;
  const [sel, setSel] = useState(hasVariants ? product.variants[0].name : "");
  const variant = hasVariants ? product.variants.find((v) => v.name === sel) : null;
  const base = product.price + Number(variant?.price_delta || 0);
  const discounted = isMember && product.member_discount_pct > 0;
  const price = discounted ? base * (1 - product.member_discount_pct / 100) : base;
  const tracked = product.type === "physical" && product.track_stock;
  const stockCount = hasVariants ? Number(variant?.stock || 0) : Number(product.stock || 0);
  const soldOut = tracked && stockCount <= 0;
  const lowStock = tracked && stockCount > 0 && stockCount <= 5;
  return (
    <div className="eyw-card overflow-hidden flex flex-col" data-testid={`product-${product.id}`}>
      <div className="relative">
        <CatalogImage src={product.image} alt={product.title} testid={`product-image-${product.id}`} />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[11px] text-[#144F75] font-medium">
          {product.type === "digital" ? <Download size={12} /> : <Package size={12} />} {product.type}
        </span>
        {soldOut ? (
          <span className="absolute top-3 right-3 inline-flex items-center rounded-full bg-[#1A242B]/90 px-2.5 py-1 text-[11px] text-white font-medium" data-testid={`stock-badge-${product.id}`}>Sold out</span>
        ) : lowStock ? (
          <span className="absolute top-3 right-3 inline-flex items-center rounded-full bg-[#D98E73] px-2.5 py-1 text-[11px] text-white font-medium" data-testid={`stock-badge-${product.id}`}>Only {stockCount} left</span>
        ) : null}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="eyw-label mb-1 text-[11px]">{product.category}</p>
          <SocialShare path={`/marketplace/${product.slug || product.id}`} ogPath={`/api/share/product/${product.id}`} title={product.title} testid={`product-share-${product.id}`} iconOnly />
        </div>
        <Link to={`/marketplace/${product.slug || product.id}`} className="text-lg text-[#1A242B] leading-tight hover:text-[#144F75] transition-colors" data-testid={`product-open-${product.id}`}>{product.title}</Link>
        <Link to={`/marketplace/${product.slug || product.id}`} className="inline-flex items-center gap-1.5 mt-1 text-xs text-[#4A5D6B] hover:text-[#144F75]" data-testid={`product-rating-${product.id}`}>
          {product.review_count > 0
            ? <><Stars value={product.avg_rating} size={12} /> {product.avg_rating} · {product.review_count} {product.review_count === 1 ? "review" : "reviews"}</>
            : <span className="text-[#8FA89B]">No reviews yet · Write one</span>}
        </Link>
        <p className="text-sm text-[#4A5D6B] mt-1.5 leading-relaxed flex-1">{product.short_description}</p>
        {hasVariants && (
          <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid={`product-variant-select-${product.id}`}
            className="mt-3 w-full rounded-lg border border-[#E5E0D8] px-3 py-2 text-sm outline-none focus:border-[#144F75] bg-white">
            {product.variants.map((v) => (
              <option key={v.name} value={v.name}>{v.name}{Number(v.price_delta) ? ` (${Number(v.price_delta) > 0 ? "+" : ""}${money(Number(v.price_delta))})` : ""}{(product.type === "physical" && product.track_stock && Number(v.stock || 0) <= 0) ? " — sold out" : ""}</option>
            ))}
          </select>
        )}
        <div className="flex items-center justify-between mt-4">
          <div className="font-serif text-xl text-[#144F75]">
            {money(price)}
            {discounted && <span className="text-xs text-[#8FA89B] ml-2 line-through">{money(base)}</span>}
          </div>
          <button onClick={() => onAdd(product, 1, variant)} disabled={soldOut} data-testid={`add-to-cart-${product.id}`} className="eyw-btn !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            <ShoppingBag size={15} /> {soldOut ? "Sold out" : "Add"}
          </button>
        </div>
        {base > 0 && base < 20 && (
          <p className="mt-2 text-[11px] text-[#8A4B2E] bg-[#FBEEE8] border border-[#E7C3B3] rounded-md px-2 py-1" data-testid={`min-cart-note-${product.id}`}>Must be part of a $20+ cart</p>
        )}
      </div>
    </div>
  );
}

export default function Marketplace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState("All");
  const { add } = useCart();
  const { user } = useAuth();
  const isMember = user && MEMBER_ROLES.includes(user.role);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: () => api.get("/products").then((r) => r.data),
    refetchInterval: 60000,
  });

  const filtered = cat === "All" ? products : products.filter((p) => p.category === cat);
  const selected = id ? products.find((p) => p.slug === id || p.id === id) : null;
  // Old ID links keep working and move to the readable address.
  useEffect(() => {
    if (selected?.slug && id === selected.id) navigate(`/marketplace/${selected.slug}`, { replace: true });
  }, [selected, id, navigate]);

  return (
    <div data-testid="marketplace-page">
      <Seo
        title={selected ? selected.title : "Marketplace"}
        description={selected
          ? (selected.short_description || stripHtml(selected.description || "", 160) || `${selected.title} — available from the Enhancing Your World marketplace.`)
          : "Physical and digital goods from Enhancers, artists, and teachers — with member discounts and fair revenue sharing."}
        image={selected?.image}
        path={selected ? `/marketplace/${selected.slug || selected.id}` : "/marketplace"}
        type={selected ? "product" : "website"}
      />
      <PageHero
        label="Marketplace"
        title="Products, teachings, and creations from the ecosystem."
        subtitle="Physical and digital goods from Enhancers, artists, and teachers — with member discounts and fair revenue sharing."
        image={HERO}
        testid="marketplace-hero"
      />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <FeaturedVendors />
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`market-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-5 py-2 text-sm transition-colors ${cat === c ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>
              {c}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-20 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-[#4A5D6B] text-center py-20">No products in this category yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {filtered.map((p) => <ProductCard key={p.id} product={p} isMember={isMember} onAdd={add} />)}
          </div>
        )}
      </section>
      <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="marketplace-gift-cards">
        <div className="max-w-2xl"><GiftCardPurchase title="Give an EYW gift card" testid="giftcert-card" /></div>
      </section>
      {selected && (
        <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/50 backdrop-blur-sm py-8 overflow-y-auto" onClick={() => navigate("/marketplace")} data-testid="product-detail-modal">
          <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl overflow-hidden my-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => navigate("/marketplace")} className="absolute top-3 right-3 z-10 bg-white/90 rounded-full h-9 w-9 grid place-items-center text-[#144F75]"><X size={18} /></button>
            <CatalogImage src={selected.image} alt={selected.title} testid="product-detail-image" />
            <div className="p-7">
              <p className="eyw-label text-[11px]">{selected.category}</p>
              <h2 className="font-serif text-2xl text-[#1A242B]">{selected.title}</h2>
              {selected.short_description && <p className="text-[#4A5D6B] mt-2 leading-relaxed">{selected.short_description}</p>}
              {selected.description && <div className="article-html text-[#4A5D6B] leading-relaxed mt-2" data-testid="product-detail-description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(selected.description) }} />}
              <div className="flex items-center justify-between mt-5">
                <span className="font-serif text-2xl text-[#144F75]">{money(Number(selected.price))}</span>
                <div className="flex items-center gap-2">
                  <SocialShare path={`/marketplace/${selected.slug || selected.id}`} ogPath={`/api/share/product/${selected.id}`} title={selected.title} testid="product-detail-share" label="Share" />
                  <button onClick={() => { add(selected, 1, null); navigate("/marketplace"); }} data-testid="product-detail-add" className="eyw-btn !py-2 text-sm"><ShoppingBag size={15} /> Add</button>
                </div>
              </div>
              <ProductReviews productId={selected.id} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
